import { useState, useEffect } from 'react';
import { ColumnDef } from '@tanstack/react-table';
import { Link, Navigate } from 'react-router-dom';
import { DictionaryPaths, ExtendedCard, Result, UserMode } from '../../types/Common';
import AutoPaginationTable from '../general/AutoPaginationTable';
import { useParams } from 'react-router-dom';
import { Form } from 'react-bootstrap';
import { useFetchOperation } from '../../service/Operation';
import Loader from '../general/Loader';
import Error from '../general/Error';
import { LocalDateTime } from '../formatters/DateTimeFormat';
import CardPanel from './CardPanel';

interface CardResultsPage {
    card: ExtendedCard
    results: UserResult[] | undefined,
    userMode: UserMode
    dictionaryPaths: DictionaryPaths,
}

interface UserResult extends Result {
    userId: number,
    userName: string,
    isDirect: boolean
}

export default function CardResults() {
    const params = useParams();

    const [pageData, setPageData] = useState<CardResultsPage>();
    const [getting, startGetting] = useFetchOperation(setPageData, undefined, true);

    useEffect(() => {
        startGetting('get', `api/cards/${params.id}/results`);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return <>
        {getting.active ?
            <Loader />
            :
            !pageData ?
                <Error text={getting.error} />
                :
                pageData?.results?.length === 1 ?
                    <Navigate to={`/results/${pageData.results[0].id}`} replace={true} />
                    :
                    <div className="width-limit">
                        <h1>Card results</h1>

                        <CardPanel card={pageData.card}
                            userMode={pageData.userMode}
                            dictionaryPaths={pageData.dictionaryPaths} />

                        <Form className="form-large width-limit mb-3">
                            <Form.Group>
                                <Form.Label>Phrase</Form.Label>
                                <div className="form-control disabled text-start">{pageData.card.phrase}</div>
                            </Form.Group>

                            {pageData.card.pronunciation &&
                                <Form.Group>
                                    <Form.Label>Pronunciation</Form.Label>
                                    <div className="form-control disabled text-start">{pageData.card.pronunciation ?? ' '}</div>
                                </Form.Group>
                            }

                            {pageData.card.forms &&
                                <Form.Group>
                                    <Form.Label>Forms</Form.Label>
                                    <div className="form-control disabled text-start">{pageData.card.forms ?? ' '}</div>
                                </Form.Group>
                            }

                            <Form.Group>
                                <Form.Label>Meaning</Form.Label>
                                <div className="form-control disabled text-start">{pageData.card.meaning}</div>
                            </Form.Group>

                            {pageData.card.example &&
                                <Form.Group>
                                    <Form.Label>Example</Form.Label>
                                    <div className="form-control disabled text-start">{pageData.card.example ?? ' '}</div>
                                </Form.Group>
                            }
                        </Form>

                        {!pageData?.results?.length ?
                            <p className="my-3">No results found</p>
                            :
                            <>
                                <span>Found {pageData.results.length} results</span>
                                <AutoPaginationTable className="my-3"
                                    columns={columns}
                                    data={pageData.results!} />
                            </>
                        }
                    </div>

        }
    </>
}

const columns: ColumnDef<UserResult>[] = [
    {
        header: 'User',
        accessorFn: r => r.userName,
        cell: props => {
            return <Link to={`/user/${props.row.original.userId}`}>{props.row.original.userName}</Link>;
        }
    },
    {
        header: 'Direction',
        accessorFn: r => r.isDirect,
        cell: props => <span>{props.row.original.isDirect ? "direct" : "inverse"}</span>
    },
    {
        header: 'Strength',
        accessorFn: r => r.strength,
        cell: props => <Link to={`/results/${props.row.original.id}`}>{Math.round(props.row.original.strength! * 10000) / 10000}</Link>
    },
    {
        header: 'Reviews',
        accessorFn: r => r.reviews
    },
    {
        header: 'Start of learning',
        accessorFn: r => r.firstUpdated,
        cell: props => <LocalDateTime value={props.row.original.firstUpdated} />
    },
    {
        header: 'Updated (Study)',
        accessorFn: r => r.interpretUpdated,
        cell: props => <LocalDateTime value={props.row.original.interpretUpdated} />
    },
    {
        header: 'Updated (Choice)',
        accessorFn: r => r.choiceUpdated,
        cell: props => <LocalDateTime value={props.row.original.choiceUpdated} />
    }
];
